/**
 * The alert slot — one position, no controls.
 *
 * `GET /alerts` is mounted. What does not exist is the screen a summary would drill
 * into for **this** rollup: Task 53 built the alert inbox, but no endpoint returns a
 * severity summary, so this stays a position that prints the request a reader can run
 * today with the screen's filter context already in it.
 *
 * ── No button, and specifically not a disabled one ──────────────────────────
 *
 * Task 43 shipped no export button because `POST /reports` was unmounted, on the
 * grounds that a button would 404 for everyone and a *disabled* one asserts a
 * capability that does not exist — "this could do something, but not for you" — when
 * the truth is that nothing could. `web/shapes/select-company.md` §2 took the same
 * ruling for `switch-company` and Task 42 for the policy acknowledgement.
 *
 * ── The AI slot has moved out of this file (Task 57) ────────────────────────
 *
 * It used to live here as a second empty position with the disclaimer over it. It is
 * now `screens/overview/AiSlot.tsx`, wired to `GET /ai/insights` and rendering the
 * very `InsightCard` the AI Insights screen uses — so an insight's `limitations`
 * reach the dashboard with it rather than being trimmed to fit a tile. The sentences
 * this file used to print about an unbuilt screen are **deleted rather than
 * softened**: a UI that keeps apologising for something somebody has finished tells
 * the reader to stop looking (G19's ruling, one screen over).
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { EmptyState } from '../../states/EmptyState';

export function PendingSlots(): ReactNode {
  const { t } = useI18n();
  return (
    <section
      className="wp-section"
      data-testid="k4-alert-slot"
      aria-labelledby="k4-alert-slot-title"
    >
      <h2 className="wp-section__title" id="k4-alert-slot-title">
        {t('overview.alerts.title')}
      </h2>
      {/* The shared empty state, not two sentences about a backlog: it names what
          is missing and where the reader reads alerts today (finish review, fix 4). */}
      <EmptyState
        title={t('overview.alerts.empty')}
        description={t('overview.alerts.body')}
        testId="k4-alert-empty"
      />
    </section>
  );
}
